import { useEffect, useRef } from "react";

import { dur, gsap } from "@/themes/rapport/motion";

const colAt = (x, y) => document.elementsFromPoint(x, y).map((el) => el.closest?.(".pl-col[data-col-id]")).find(Boolean) || null;

// Glisser-déposer à la souris ou au doigt : fantôme incliné qui suit le pointeur, colonne cible éclairée,
// retour animé à la place d'origine si le lâcher n'aboutit pas (hors colonne, même colonne, Échap)
export function useCardDrag(rootRef, onDrop) {
  const cb = useRef(onDrop);
  cb.current = onDrop;
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let s = null;
    const setOver = (col) => {
      if (s.over === col) return;
      s.over?.classList.remove("is-over");
      s.over = col && col.dataset.colId !== s.from ? col : null;
      s.over?.classList.add("is-over");
    };
    const begin = () => {
      const r = s.card.getBoundingClientRect();
      s.rect = r;
      s.ghost = s.card.cloneNode(true);
      s.ghost.removeAttribute("data-testid");
      s.ghost.removeAttribute("data-lead-id");
      s.ghost.classList.add("pl-ghost");
      Object.assign(s.ghost.style, { position: "fixed", left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, margin: 0, zIndex: 9999, pointerEvents: "none" });
      // enveloppe data-view="pipeline" : le fantôme garde les couleurs du thème de la carte
      s.wrap = Object.assign(document.createElement("div"), { className: "pl-ghost-wrap" });
      s.wrap.dataset.view = "pipeline";
      s.wrap.appendChild(s.ghost);
      document.body.appendChild(s.wrap);
      gsap.to(s.ghost, { rotation: 3, scale: 1.03, boxShadow: "0 28px 56px rgba(20,18,16,.30)", duration: dur(0.18) });
      s.card.classList.add("is-placeholder");
      document.body.classList.add("pl-dragging");
      s.active = true;
    };
    const finish = () => {
      document.body.classList.remove("pl-dragging");
      s.over?.classList.remove("is-over");
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", esc, true);
    };
    const back = ({ ghost, card, rect }) => {
      const now = card.isConnected ? card.getBoundingClientRect() : rect;
      gsap.to(ghost, { left: now.left, top: now.top, x: 0, y: 0, rotation: 0, scale: 1, boxShadow: "none", duration: dur(0.35), ease: "power3.out", onComplete: () => { ghost.parentNode?.remove(); card.classList.remove("is-placeholder"); } });
    };
    const move = (e) => {
      if (!s) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (!s.active) { if (Math.hypot(dx, dy) < 6) return; begin(); window.dispatchEvent(new Event("pl-drag-start")); }
      gsap.set(s.ghost, { x: dx, y: dy });
      setOver(colAt(e.clientX, e.clientY));
    };
    const up = (e) => {
      if (!s) return;
      const st = s;
      finish();
      s = null;
      if (!st.active) return;
      // le clic qui suit un glisser n'ouvre pas la fiche
      const block = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      window.addEventListener("click", block, { capture: true, once: true });
      setTimeout(() => window.removeEventListener("click", block, { capture: true }), 300);
      const col = colAt(e.clientX, e.clientY);
      const ok = col && col.dataset.colId !== st.from && cb.current(st.id, Number(col.dataset.colId), st.ghost.getBoundingClientRect());
      if (ok) {
        st.wrap.remove();
        st.card.classList.remove("is-placeholder");
      } else back(st);
    };
    const cancel = () => { if (!s) return; const st = s; finish(); s = null; if (st.active) back(st); };
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); cancel(); } };
    const down = (e) => {
      if (e.button !== 0 || e.pointerType === "touch" || e.target.closest("button, a, input, select, textarea, [data-no-drag]")) return;
      const card = e.target.closest(".pl-card[data-lead-id]");
      if (!card || !root.contains(card)) return;
      s = { card, id: Number(card.dataset.leadId), from: card.closest(".pl-col")?.dataset.colId, x: e.clientX, y: e.clientY, active: false, over: null };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("keydown", esc, true);
    };
    root.addEventListener("pointerdown", down);
    return () => { root.removeEventListener("pointerdown", down); if (s) cancel(); };
  }, [rootRef]);
}
